<template>
  <section class="table-wrap">
    <div class="url-container">
      <p class="url-text">
        {{$t('WEBHOOKS_SEND_MESSAGES.LIST.WEBHOOK_URL_LABEL')}} {{ webhook }}
      </p>
      <WootButton
        variant="link"
        icon="copy"
        color-scheme="secondary"
        @click="() => this.copyToClipboard(webhook)"
      >
      </WootButton>
    </div>
    <div v-if="currentUser.type === 'SuperAdmin'" class="webhook-log-link">
      <a :href="urlLogs" class="webhook-log-link-a" target="_blank" rel="noopener">
        {{ $t('WEBHOOKS_SEND_MESSAGES.LIST.VIEW_LOG') }}
      </a>
    </div>
    <ve-table :fixed-header="true" scroll-width="90rem" :columns="columns" :table-data="tableData"
      :border-around="false" :sort-option="sortOption" />

    <empty-state v-if="showSearchEmptyState" :title="$t('WEBHOOKS_SEND_MESSAGES.LIST.NOT_FOUND')" />

    <empty-state v-else-if="!isLoading && !items.length" :title="$t('WEBHOOKS_SEND_MESSAGES.LIST.404')" />

    <div v-if="isLoading" class="item--loader">
      <spinner />
      <span>{{ $t('WEBHOOKS_SEND_MESSAGES.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import { VeTable } from 'vue-easytable';
import md5 from 'blueimp-md5';

import Spinner from 'shared/components/Spinner.vue';
import Taggle from 'shared/components/Taggle.vue';
import WootButton from 'dashboard/components/ui/WootButton';
import WootSwitch from 'components/ui/Switch';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { mapGetters } from 'vuex';

import { frontendURL } from 'dashboard/helper/URLHelper';

export default {
  components: {
    Spinner,
    WootButton,
    WootSwitch,
    EmptyState,
    VeTable,
    Taggle,
  },
  mixins: [clickaway, rtlMixin, alertMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    showSearchEmptyState: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortChange: params => this.$emit('on-sort-change', params),
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    tableData() {
      if (this.isLoading) {
        return [];
      }

      return this.items;
    },
    columns() {
      return [
        {
          field: 'name',
          key: 'name',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.NAME'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.name || '',
          width: 100,
        },
        {
          field: 'template',
          key: 'template',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.TEMPLATE'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (
              <div>
                <span>{row.actions.map((action) => action.template_params.name).join(', ')}</span>
              </div>
            );
          },
        },
        {
          field: 'consumer_phonenumber',
          key: 'consumer_phonenumber',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.CONSUMER_PHONENUMBER'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (
              <div>
                <span>{row.consumer_phonenumber}</span>
              </div>
            );
          },
        },
        {
          field: 'units',
          key: 'units',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.UNITS'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (
              <div>
                <Taggle rows={row.units}/>
              </div>
            );
          },
        },
        {
          field: 'users',
          key: 'users',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.USERS'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            if(!row.users || row.users.length == 0) {
              return '';
            }
            return (
              <div>
                <Taggle rows={row.users}/>
              </div>
            );
          },
        },
        {
          field: 'type_rules',
          key: 'type_rules',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.TYPE_RULES'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (
              <div>
                <Taggle rows={row.type_rules}/>
              </div>
            );
          },
        },
        {
          field: 'enabled',
          key: 'enlabed',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.ENABLED'),
          align: 'left',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (
              <div>
                <WootSwitch
                  value={row.enabled}
                  onInput={value => this.$emit('on-switch-change', { ...row, enabled: value })}
                />
              </div>
            );
          },
        },
        {
          field: 'actions',
          key: 'actions',
          title: this.$t('WEBHOOKS_SEND_MESSAGES.LIST.HEADER.ACTIONS'),
          align: 'center',
          width: 100,
          renderBodyCell: ({ row }) => {
            return (            
              <div class="button-wrapper">
                <WootButton
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="edit"
                  onClick={() => this.$emit('on-edit-click', row)}                  
                >
                  {this.$t('WEBHOOKS_SEND_MESSAGES.EDIT.BUTTON_TEXT')}
                </WootButton>
                <WootButton
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  onClick={() => this.$emit('on-delete-click', row)}                  
                >                  
                  {this.$t('WEBHOOKS_SEND_MESSAGES.DELETE.BUTTON_TEXT')}
                </WootButton> 
              </div>            
            )
          },
        },
      ];
    },
    webhook() {
      return `${window.location.origin}/webhooks/send_messages/${this.$route.params.partner}/${md5(this.accountId)}`;
    },
    urlLogs() {
      return frontendURL(`accounts/${this.accountId}/settings/applications/webhooks-send-messages/logs`);
    }
  },
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.LIST.URL_COPY.SUCCESS'));
      }).catch(err => {
        this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.LIST.URL_COPY.ERROR'));
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';

.table-wrap {
  flex: 1 1;
  height: 100%;
  overflow: hidden;

  .url-container {
    display: flex;
    align-items: center;

    .url-text {
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .webhook-log-link {
    margin-top: var(--space-small);
    margin-bottom: var(--space-medium);
    
    .webhook-log-link-a {
      color: var(--w-600);
      text-decoration: none;
      font-weight: var(--font-weight-medium);
      
      &:hover {
        color: var(--w-800);
        text-decoration: underline;
      }
    }
  }
}

.table-wrap::v-deep {
  .ve-table {
    padding-bottom: var(--space-large);

    .ve-table-container {
      overflow-y: hidden !important;
    }
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
    font-size: var(--font-size-mini) !important;
  }

  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;

    a {
      font-weight: var(--font-weight-medium);
    }
  }

  .ve-table-sort {
    top: -4px;
  }
  
}

.item--loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-big);
}

.ve-table-content{
  .button-wrapper {
    display: flex;
    justify-content: center;
    gap: var(--space-small);
  }
}
</style>